import * as React from "react";
import { Badge as UIBadge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

const baseBadgeVariantMap = {
	default: "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
	secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
	destructive: "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
	outline: "text-foreground",
	accent: "border-transparent bg-blue-400 text-white hover:bg-blue-500",
	accentOutline:
		"border border-blue-300 text-blue-400 bg-white hover:border-blue-450 hover:text-blue-400 hover:bg-blue-50",
	dark: "border-transparent bg-black text-white hover:bg-gray-900",
};

const extendedBadgeVariants = cva(
	"inline-flex items-center leading-none rounded-md border gap-1.5 px-2 py-0 text-[10px]",
	{
		variants: {
			variant: {
				...baseBadgeVariantMap,
				success: "border-transparent bg-green-500 text-white hover:bg-green-600",
				warning: "border-transparent bg-yellow-400 text-black hover:bg-yellow-500",
				blocked: "border-transparent bg-red-500 text-white hover:bg-red-600",
				full: "border-transparent bg-indigo-500 text-white hover:bg-indigo-600",
				unknown: "bg-muted text-muted-foreground border border-border",
				undetermined:
					"border-transparent bg-gray-200 text-gray-800 hover:bg-gray-300 " +
					"dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

export type BadgeVariant = NonNullable<VariantProps<typeof extendedBadgeVariants>["variant"]>;

type NativeBadgePropsWithoutVariant = Omit<React.ComponentProps<typeof UIBadge>, "variant">;

export interface BadgeProps extends NativeBadgePropsWithoutVariant, VariantProps<typeof extendedBadgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
	return <UIBadge className={cn(extendedBadgeVariants({ variant }), className)} {...props} />;
}
